import { LoadStatus, StopKind } from "../enums.js";
/** Real stops in order, or pickup/drop derived from the load's origin/destination. */
export function progressStops(load, stops) {
    const live = stops.filter((s) => s.deletedAt == null).sort((a, b) => a.seq - b.seq);
    if (live.length > 0) {
        return live.map((s) => ({
            id: s.id,
            seq: s.seq,
            kind: s.kind,
            place: s.place,
            arrivedAt: s.arrivedAt,
            departedAt: s.departedAt,
        }));
    }
    return [
        { id: null, seq: 1, kind: StopKind.pickup, place: load.origin, arrivedAt: null, departedAt: null },
        { id: null, seq: 2, kind: StopKind.drop, place: load.destination, arrivedAt: null, departedAt: null },
    ];
}
function placeName(p, fallback) {
    return p.text?.trim() || fallback;
}
export function loadProgress(load, rawStops, opts) {
    const stops = progressStops(load, rawStops);
    const lastDropIdx = (() => {
        for (let i = stops.length - 1; i >= 0; i--)
            if (stops[i].kind === StopKind.drop)
                return i;
        return stops.length - 1;
    })();
    const base = { stops, next: null, back: null, canCancel: false, awaitingPay: false };
    switch (load.status) {
        case LoadStatus.booked:
            return {
                ...base,
                phase: "Booked",
                next: { kind: "start", label: "Start: at pickup", stop: 0 },
                back: { kind: "unbook", label: "Not booked after all" },
                canCancel: true,
            };
        case LoadStatus.rolling: {
            // First stop still open (not left), in order.
            const i = stops.findIndex((s) => s.departedAt == null);
            let next = null;
            let phase = "Rolling";
            if (i >= 0) {
                const s = stops[i];
                const name = placeName(s.place, s.kind === StopKind.pickup ? "pickup" : "delivery");
                if (s.arrivedAt == null) {
                    next = { kind: "arrive", label: s.kind === StopKind.pickup ? "At pickup" : "At delivery", stop: i };
                    phase = i === 0 ? "Rolling to pickup" : `Loaded — heading to ${name}`;
                }
                else if (i === lastDropIdx) {
                    phase = "At delivery";
                    next = opts.hasPod
                        ? { kind: "depart", label: "Delivered", stop: i, delivers: true }
                        : { kind: "need_pod", label: "Add POD to deliver", stop: i };
                }
                else {
                    phase = s.kind === StopKind.pickup ? "At pickup" : "At stop";
                    next = {
                        kind: "depart",
                        label: s.kind === StopKind.pickup ? "Loaded — leaving pickup" : "Unloaded — leaving stop",
                        stop: i,
                        delivers: false,
                    };
                }
            }
            else {
                // Every stop left but still rolling (e.g. delivered was undone): deliver again.
                phase = "At delivery";
                next = opts.hasPod
                    ? { kind: "depart", label: "Delivered", stop: lastDropIdx, delivers: true }
                    : { kind: "need_pod", label: "Add POD to deliver", stop: lastDropIdx };
            }
            // Back: the most recent timestamp.
            let back = null;
            for (let j = stops.length - 1; j >= 0 && !back; j--) {
                const s = stops[j];
                if (s.departedAt)
                    back = { kind: "undo_depart", label: "Undo last step", stop: j };
                else if (s.arrivedAt)
                    back = { kind: "undo_arrive", label: "Undo last step", stop: j };
            }
            return { ...base, phase, next, back, canCancel: true };
        }
        case LoadStatus.delivered:
            return {
                ...base,
                phase: "Delivered — waiting on pay",
                back: { kind: "undo_delivered", label: "Undo delivered", stop: stops[lastDropIdx]?.departedAt ? lastDropIdx : null },
                awaitingPay: true,
            };
        case LoadStatus.paid:
            return { ...base, phase: "Paid" };
        case LoadStatus.cancelled:
            return { ...base, phase: "Cancelled", back: { kind: "reopen", label: "Reopen load" } };
        case LoadStatus.passed:
            return { ...base, phase: "Passed", back: { kind: "reopen", label: "Reopen load" } };
        default:
            return { ...base, phase: "Considering" };
    }
}
/** Mon–Sun pay week (blueprint: Settlement Mon-Sun org tz) containing `instant`. */
export function settlementWeek(instant, timeZone) {
    const d = typeof instant === "string" ? new Date(instant) : instant;
    let tz = timeZone;
    try {
        new Intl.DateTimeFormat("en-US", { timeZone: tz });
    }
    catch {
        tz = "UTC";
    }
    const parts = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
    const local = new Date(`${parts}T00:00:00Z`);
    const dow = local.getUTCDay(); // 0 Sun … 6 Sat
    const sinceMonday = (dow + 6) % 7;
    const start = new Date(local.getTime() - sinceMonday * 86_400_000);
    const end = new Date(start.getTime() + 6 * 86_400_000);
    return { periodStart: start.toISOString().slice(0, 10), periodEnd: end.toISOString().slice(0, 10) };
}
/** Next week's Monday for a 'YYYY-MM-DD' Monday. */
export function nextWeekStart(periodStart) {
    const s = new Date(`${periodStart}T00:00:00Z`).getTime() + 7 * 86_400_000;
    return {
        periodStart: new Date(s).toISOString().slice(0, 10),
        periodEnd: new Date(s + 6 * 86_400_000).toISOString().slice(0, 10),
    };
}
//# sourceMappingURL=progress.js.map